import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import { ApiError } from '../lib/api.js';
import { Button, Field, inputClass } from '../components/ui.js';

// Specific, localized messages per server error code — so the user knows
// whether to ask for a new invite, log in instead, or fix the password.
const MSG: Record<string, { fr: string; en: string }> = {
  invite_invalid: { fr: 'Lien d’invitation invalide. Vérifiez l’adresse complète ou demandez une nouvelle invitation.', en: 'Invalid invitation link. Check the full address or ask for a new invitation.' },
  invite_used: { fr: 'Cette invitation a déjà été utilisée. Connectez-vous, ou demandez une nouvelle invitation.', en: 'This invitation was already used. Sign in, or ask for a new invitation.' },
  invite_expired: { fr: 'Cette invitation a expiré. Demandez une nouvelle invitation à l’administrateur.', en: 'This invitation has expired. Ask your admin for a new one.' },
  account_exists: { fr: 'Un compte existe déjà avec cet e-mail ou ce téléphone. Connectez-vous plutôt.', en: 'An account with this email or phone already exists. Sign in instead.' },
  password_required: { fr: 'Veuillez créer un mot de passe (au moins 12 caractères).', en: 'Please create a password (at least 12 characters).' },
};

export function AcceptInvite() {
  const [params] = useSearchParams();
  const { acceptInvite } = useAuth();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const lang = locale === 'fr' ? 'fr' : 'en';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await acceptInvite(token, password || undefined);
      navigate('/');
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      const mapped = MSG[code]?.[lang];
      setError(mapped ?? (locale === 'fr' ? 'Invitation invalide ou expirée' : 'Invalid or expired invite'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <form onSubmit={submit} className="bg-white p-8 rounded-xl shadow-md w-full max-w-sm border border-slate-200">
        <h1 className="text-xl font-bold text-brand-dark mb-4">{locale === 'fr' ? 'Accepter l’invitation' : 'Accept invitation'}</h1>
        {!token && <p className="text-danger text-sm">{locale === 'fr' ? 'Lien invalide' : 'Invalid link'}</p>}
        <Field label={locale === 'fr' ? 'Créer un mot de passe' : 'Create a password'}>
          <input className={inputClass} type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={12} />
        </Field>
        {error && <p className="text-danger text-sm mb-3">{error}</p>}
        <Button type="submit" disabled={busy || !token} className="w-full">{busy ? '…' : 'OK'}</Button>
      </form>
    </div>
  );
}
